import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { FaCalendarAlt, FaEye } from "react-icons/fa";
import styles from "../List.module.css";
import AxiosInstance, { TokenService } from "../../../../services/Api";
import {
  CustomButton,
  CustomDropdown,
  CustomTextInput,
} from "../../../../components";
import CustomMRTTable from "../../../../components/CustomMRTTable/CustomMRTTable";

const ExamRulesList = () => {
  const navigate = useNavigate();
  const user = TokenService.getUser();
  const clientData = user?.client_data[0] || {};
  const school_id = clientData?.school_id || "";
  const academic_year = clientData?.academic_year || "";
  const academic_year_id = clientData?.academic_year_id || "";

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [apiSearch, setApiSearch] = useState("");
  const [rowCount, setRowCount] = useState(0);
  const [examNameOptions, setExamNameOptions] = useState([]);
  const [selectedExamName, setSelectedExamName] = useState("");
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const { translations } = useSelector((state) => state.language);

  // Fetch exam names on mount
  useEffect(() => {
    if (school_id && academic_year_id) {
      fetchExamNames();
    }
  }, [school_id, academic_year_id]);

  // Fetch exam rules list when filters change
  useEffect(() => {
    if (school_id && academic_year_id) {
      fetchExamRulesList();
    }
  }, [
    school_id,
    academic_year_id,
    selectedExamName,
    pagination.pageIndex, // Add this
    pagination.pageSize, // Add this
    apiSearch,
  ]);

  // Fetch exam names for dropdown
  const fetchExamNames = useCallback(async () => {
    try {
      const payload = {
        school_id: parseInt(school_id),
        academic_year: parseInt(academic_year_id),
      };

      const response = await AxiosInstance.post(
        `/exam/examConfig/list`,
        payload,
      );

      if (response?.data?.status) {
        const examData = response.data.data || [];
        const options = [
          { value: "", label: translations.labels?.allExams || "All Exams" },
          ...examData.map((item) => ({
            value: item.id,
            label: item.exam_type || item.exam_name,
          })),
        ];
        setExamNameOptions(options);
      }
    } catch (error) {
      console.error("Error fetching exam names:", error);
      setExamNameOptions([
        { value: "", label: translations.labels?.allExams || "All Exams" },
      ]);
    }
  }, [school_id, academic_year_id, translations.labels?.allExams]);

  // Fetch exam rules list
  const fetchExamRulesList = useCallback(async () => {
    setLoading(true);
    try {
      const payload = {
        school_id: parseInt(school_id),
        academic_year: parseInt(academic_year_id),
        config_id: parseInt(selectedExamName?.value) || "",
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
      };

      const response = await AxiosInstance.post(
        `/exam/examRules/list`,
        payload,
      );

      if (response?.data?.status) {
        // FIX: The data is directly in response.data.data (array), not response.data.data.rows
        const rulesData = response?.data?.data || [];

        const transformedData = rulesData.map((item, index) => ({
          id: item.id,
          sno: pagination.pageIndex * pagination.pageSize + index + 1,
          academic_year_id: item.academic_year_id,
          academic_year: item.academic_year || academic_year,
          exam_type: item.exam_type,
          exam_config_id: item.campus_exam_config_id,
          theory: item.theory ? "Yes" : "No",
          practical: item.practical ? "Yes" : "No",
          theoryValue: item.theory,
          practicalValue: item.practical,
          status: item.is_active ? "Active" : "Inactive",
          isActive: item.is_active,
        }));

        setData(transformedData);
        // Set total count from pagination response
        if (response?.data?.pagination) {
          setRowCount(response.data.pagination.total || 0);
        } else {
          setRowCount(transformedData.length);
        }
        setRowCount(transformedData.length);
      } else {
        setData([]);
        setRowCount(0);
      }
    } catch (error) {
      console.error("Error fetching exam rules list:", error);
      setData([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  }, [
    school_id,
    academic_year_id,
    academic_year,
    selectedExamName,
    pagination.pageIndex, // Add this
    pagination.pageSize, // Add this
    apiSearch, // Add this if you're using it
  ]);
  // Navigation handlers
  const handleAdd = useCallback(() => {
    navigate("/user/examRulesCreate");
  }, [navigate]);

  const handleView = useCallback(
    (row) => {
      navigate("/user/examRulesCreate", {
        state: {
          ID: row.id,
          data: row,
        },
      });
    },
    [navigate],
  );

  // Table columns
  const columns = useMemo(
    () => [
      {
        accessorKey: "sno",
        header: translations.labels?.serialNo || "S.No",
        size: 80,
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "academic_year",
        header: translations.labels?.academicYear || "Academic Year",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        accessorKey: "exam_type",
        header: translations.labels?.examName || "Exam Name",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        accessorKey: "theory",
        header: translations.labels?.theory || "Theory",
        enableSorting: true,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <span
            className={
              row.original.theoryValue ? styles.statusYes : styles.statusNo
            }
          >
            {row.original.theory}
          </span>
        ),
      },
      {
        accessorKey: "practical",
        header: translations.labels?.practical || "Practical",
        enableSorting: true,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <span
            className={
              row.original.practicalValue ? styles.statusYes : styles.statusNo
            }
          >
            {row.original.practical}
          </span>
        ),
      },
      {
        accessorKey: "status",
        header: translations.labels?.status || "Status",
        enableSorting: true,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <span
            className={
              row.original.isActive
                ? styles.statusActive
                : styles.statusInactive
            }
          >
            {row.original.status}
          </span>
        ),
      },
      {
        id: "actions",
        header: translations.labels?.actions || "Actions",
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <button
            className={styles.viewBtn}
            onClick={() => handleView(row.original)}
            title={translations.labels?.view || "View"}
          >
            <FaEye />
          </button>
        ),
      },
    ],
    [translations, handleView],
  );

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerIcon}>
          <FaCalendarAlt />
        </div>
        <h1 className={styles.headerTitle}>
          {translations.labels?.examRules || "Exam Rules"}
        </h1>
      </div>

      {/* Filters and Add Button */}
      <div className={styles.filterSection}>
        <div className="row align-items-end">
          {/* Academic Year - Read Only */}
          <div className="col-lg-3 px-2 col-md-6 col-sm-12">
            <CustomTextInput
              label={translations.labels?.academicYear || "Academic Year"}
              name="academicYear"
              type="text"
              value={academic_year}
              disabled
              readOnly
            />
          </div>

          {/* Exam Name Dropdown */}
          <div className="col-lg-4 px-2 col-md-6 col-sm-12">
            <CustomDropdown
              label={translations.labels?.examName || "Exam Name"}
              name="examName"
              value={selectedExamName}
              onChange={(value) => setSelectedExamName(value)}
              options={examNameOptions}
              placeholder={
                translations.labels?.selectExamName || "Select Exam Name"
              }
            />
          </div>

          {/* Add Button */}
          <div className="col-lg-5 px-2 col-md-12 col-sm-12">
            <div className={styles.actions}>
              <CustomButton
                type="button"
                variant="contained"
                className={styles.addButton}
                fullWidth={false}
                onClick={handleAdd}
              >
                {translations.labels?.add || "ADD"}
              </CustomButton>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <CustomMRTTable
        columns={columns}
        data={data} // Use data directly (not paginatedData)
        loading={loading}
        pagination={pagination}
        rowCount={rowCount} // Use the total from API
        onPaginationChange={setPagination}
        searchInput={searchInput}
        enableSearch={false}
        enableFiltering={false}
        setSearchInput={setSearchInput}
        onSearchEnter={(value) => {
          setApiSearch(value);
          setPagination((prev) => ({ ...prev, pageIndex: 0 }));
        }}
        manualPagination={true} // Add this prop
      />
    </div>
  );
};

export default ExamRulesList;
